import { showAlert } from "@/lib/alert";
import { showToast } from "@/lib/toast";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Application from "expo-application";
import * as Device from "expo-device";
import { Linking, Platform } from "react-native";
import { checkVersion } from "react-native-check-version";

const LAST_CHECK_KEY = "last-store-update-check-at";
export const STORE_UPDATE_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

type CheckStoreUpdateOptions = {
  /** When true, skip the "latest version" toast (used by the daily auto-check). */
  silentIfUpToDate?: boolean;
};

export function canRunStoreUpdateCheck(
  platform: string = Platform.OS,
  isDevice: boolean = Device.isDevice,
): boolean {
  return platform !== "web" && isDevice;
}

export function isStoreUpdateCheckDue(
  lastCheckAt: number | null,
  now: number = Date.now(),
): boolean {
  if (!lastCheckAt) {
    return true;
  }

  return now - lastCheckAt >= STORE_UPDATE_CHECK_INTERVAL_MS;
}

/**
 * Compares the installed app version against the App Store / Play Store listing.
 * Shows an Update/Later alert when an update is available.
 */
export async function checkStoreUpdate(
  options: CheckStoreUpdateOptions = {},
): Promise<void> {
  const { silentIfUpToDate = false } = options;

  if (!canRunStoreUpdateCheck()) {
    return;
  }

  const version = await checkVersion({
    bundleId: Application.applicationId || "",
    currentVersion: Application.nativeApplicationVersion || "",
  });

  if (version.needsUpdate) {
    showAlert(
      "Update Available",
      `A new version (${version.version}) is available. Please update to the latest version.`,
      [
        {
          text: "Update",
          onPress: () => {
            if (version.url) {
              Linking.openURL(version.url);
            }
          },
        },
        { text: "Later", style: "cancel" },
      ],
      { cancelable: false },
    );
    return;
  }

  if (!silentIfUpToDate) {
    showToast.info("You are using the latest version.");
  }
}

/**
 * Runs a store update check at most once every 24 hours.
 * Intended for cold start / AppState active — does not toast when up to date.
 */
export async function maybeCheckStoreUpdateDaily(): Promise<void> {
  if (!canRunStoreUpdateCheck()) {
    return;
  }

  try {
    const lastCheckRaw = await AsyncStorage.getItem(LAST_CHECK_KEY);
    const lastCheckAt = lastCheckRaw ? Number(lastCheckRaw) : null;

    if (!isStoreUpdateCheckDue(lastCheckAt)) {
      return;
    }

    await checkStoreUpdate({ silentIfUpToDate: true });
    await AsyncStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
  } catch (error) {
    console.error("Error checking for store update:", error);
  }
}
